import { css, keyframes } from '@emotion/react';

import NextImage, { ImageProps as NextImageProps } from 'next/image';

import React from 'react';

import { IEFinderObject } from '../../model';
import { CSSProps } from '../../utils/misc';

type Props = {
  image?: IEFinderObject<'image'>;
  h?: number;
  radius?: string | number;
  children?: React.ReactNode;
};

type ImageProps = Pick<
  NextImageProps,
  | 'width'
  | 'height'
  | 'layout'
  | 'objectFit'
  | 'objectPosition'
  | 'alt'
  | 'onClick'
  | 'loading'
  | 'priority'
>;

const fadeInKeyframes = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const imageCSS = (radius?: string | number) => css`
  animation: ${fadeInKeyframes} 0.5s linear forwards;
  overflow: hidden;
  border-radius: inherit;
  ${radius &&
  css`
    border-radius: ${typeof radius === 'string' ? radius : `${radius}px`};
  `}
`;

const ratio = 2;

const EFinderImage = ({
  className,
  image,
  h,
  radius,
  width,
  height,
  alt,
  objectFit,
  objectPosition,
  loading,
  layout,
  priority,
  ...rest
}: CSSProps & Props & ImageProps) => {
  if (!image || !image.attachment) {
    return null;
  }

  const { url, contentType, height: attachmentHeight } = image.attachment;

  const bgUrl =
    contentType === 'image/gif' || h === undefined
      ? url
      : `${url}?t=h&d=${h ? h * ratio : attachmentHeight}`;

  return (
    <NextImage
      css={imageCSS(radius)}
      className={className}
      {...rest}
      src={bgUrl}
      width={width}
      height={height}
      layout={layout || 'fill'}
      loading={loading || 'lazy'}
      objectFit={objectFit || 'cover'}
      alt={alt}
      objectPosition={objectPosition || 'center'}
      unoptimized
      priority={!!priority}
    />
  );
};

export const OptimizeEFinderImage = ({
  className,
  image,
  h,
  radius,
  width,
  height,
  alt,
  objectFit,
  objectPosition,
  loading,
  layout,
  priority,
  ...rest
}: CSSProps & Props & ImageProps) => {
  if (!image || !image.attachment) {
    return null;
  }

  const { url, contentType, height: attachmentHeight } = image.attachment;

  const myLoader = ({
    src,
    quality = 75,
  }: {
    src: string;
    quality?: number;
  }) => {
    return `${src}&q=${quality}`;
  };

  const bgUrl =
    contentType === 'image/gif' || h === undefined
      ? url
      : `${url}?t=h&d=${h ? h * ratio : attachmentHeight}`;

  return (
    <NextImage
      css={imageCSS(radius)}
      className={className}
      {...rest}
      src={bgUrl}
      width={width}
      height={height}
      loader={myLoader}
      layout={layout || 'fill'}
      loading={loading || (priority ? 'eager' : 'lazy')}
      objectFit={objectFit || 'cover'}
      alt={alt}
      objectPosition={objectPosition || 'center'}
      priority={!!priority}
    />
  );
};

export default EFinderImage;

export const enum EFinderImageSizes {
  marketItem = 80,
  banner = 420,
  avatar = 64,
  chatbot = 150,
}
